import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { Rect } from 'dragonmouse-shared/geometry';
import { Frame, FrameType } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import React, { useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Direction, getFrameBounds, getFrameIconType } from 'redux/db/util';
import { selectTransform } from 'redux/mode/reducers';
import { getCanvasRect } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { unthemed } from 'tokens';
import * as S from './styles';

enum ControlType {
	PROTOTYPE,
	FRAME,
	NONE,
}

interface Props {
	frame: Frame;
}
const FrameControls = ({ frame }: Props) => {
	const [tooltipId] = useState(String(Math.random()));

	const { transform } = useSelector(
		(r: RootReducer) => ({
			transform: selectTransform(r.mode),
		}),
		shallowEqual
	);

	const bounds = useMemo(() => {
		const bounds = getFrameBounds(frame);
		return getCanvasRect(bounds);
	}, [frame.id, transform]);

	//------------------------------------------------------------------------------------------------
	// STYLE GEOMETRY
	//------------------------------------------------------------------------------------------------
	const getMaskStyles = () => {
		// Get canvas dimensions - assuming it fills the viewport
		const canvasWidth = window.innerWidth;
		const canvasHeight = window.innerHeight;

		return {
			top: {
				left: 0,
				top: 0,
				width: canvasWidth,
				height: Math.max(0, bounds.y),
			},
			bottom: {
				left: 0,
				top: bounds.y + bounds.height,
				width: canvasWidth,
				height: Math.max(0, canvasHeight - (bounds.y + bounds.height)),
			},
			left: {
				left: 0,
				top: bounds.y,
				width: Math.max(0, bounds.x),
				height: bounds.height,
			},
			right: {
				left: bounds.x + bounds.width,
				top: bounds.y,
				width: Math.max(0, canvasWidth - (bounds.x + bounds.width)),
				height: bounds.height,
			},
		};
	};

	const getFrameStyles = (rect: Rect) => {
		const frameWidth = 12;
		const topFrameHeight = 32;
		const bottomFrameHeight = 22;

		return {
			top: {
				left: rect.x - frameWidth,
				top: rect.y - topFrameHeight,
				width: rect.width + frameWidth * 2,
				height: topFrameHeight,
			},
			bottom: {
				left: rect.x - frameWidth,
				top: rect.y + rect.height,
				width: rect.width + frameWidth * 2,
				height: bottomFrameHeight,
			},
			left: {
				left: rect.x - frameWidth,
				top: rect.y,
				width: frameWidth,
				height: rect.height,
			},
			right: {
				left: rect.x + rect.width,
				top: rect.y,
				width: frameWidth,
				height: rect.height,
			},
		};
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderAction = (action: MenuItemAction, tooltipContent: string, iconType: IconType) => {
		const iconSize = 11;

		return (
			<S.Action
				onClick={() => action && !action.disabled && action.callback()}
				disabled={!action || action.disabled}
			>
				<a data-tooltip-id={tooltipId} data-tooltip-content={tooltipContent}>
					<Icon type={iconType} width={iconSize} height={iconSize} color={unthemed.gray[0]} />
				</a>
			</S.Action>
		);
	};

	const renderAction2 = (action: MenuItemAction, tooltipContent: string, iconType: IconType) => {
		const iconSize = 11;

		return (
			<S.Action2
				onClick={() => action && !action.disabled && action.callback()}
				disabled={!action || action.disabled}
			>
				<a data-tooltip-id={tooltipId} data-tooltip-content={tooltipContent}>
					<S.IconWrapper>
						<Icon type={iconType} width={iconSize} height={iconSize} color={unthemed.gray[0]} />
					</S.IconWrapper>
				</a>
			</S.Action2>
		);
	};

	const renderFrameActions = () => {
		const previewAction = frame.id ? MenuItemAction.createOpenPrototype(frame.id) : null;

		return (
			<S.ActionsBox
				style={{
					gap: '4px',
				}}
			>
				{previewAction && renderAction(previewAction, 'View prototype (P)', IconType.PLAY)}
				<Tooltip id={tooltipId} placement="top" />
			</S.ActionsBox>
		);
	};

	const renderArrowActions = () => {
		const leftAction = frame.id
			? MenuItemAction.createNavFromFrame(frame.id, Direction.LEFT)
			: null;
		const rightAction = frame.id
			? MenuItemAction.createNavFromFrame(frame.id, Direction.RIGHT)
			: null;
		const upAction = frame.id ? MenuItemAction.createNavFromFrame(frame.id, Direction.UP) : null;
		const downAction = frame.id
			? MenuItemAction.createNavFromFrame(frame.id, Direction.DOWN)
			: null;

		return (
			<S.ActionsBox2>
				{leftAction !== null &&
					renderAction2(leftAction!, 'Focus left (left arrow key)', IconType.LINE_ARROW_LEFT)}
				{rightAction !== null &&
					renderAction2(rightAction!, 'Focus right (right arrow key)', IconType.LINE_ARROW_RIGHT)}
				<S.ActionsBox2Spacer />
				{upAction !== null &&
					renderAction2(upAction!, 'Focus up (up arrow key)', IconType.LINE_ARROW_UP)}
				{downAction !== null &&
					renderAction2(downAction!, 'Focus down (down arrow key)', IconType.LINE_ARROW_DOWN)}
			</S.ActionsBox2>
		);
	};

	let controlType = ControlType.NONE;
	if (frame.metadata.type && transform.scale > 0.1) {
		switch (frame.metadata.type) {
			case FrameType.PROTOTYPE:
			case FrameType.MERGE:
			case FrameType.GENERATE:
			case FrameType.GENERATE_FROM_CHAT:
			case FrameType.ENHANCE:
			case FrameType.ADJUST:
			case FrameType.IMPORT_SITE:
				controlType = ControlType.FRAME;
				break;
			case FrameType.IMPORT:
			default:
				controlType = ControlType.NONE;
				break;
		}
	}

	const prototype = frame.metadata.type === FrameType.PROTOTYPE;

	return (
		<React.Fragment>
			{(() => {
				const maskStyles = getMaskStyles();
				return (
					<>
						<S.Mask style={maskStyles.top} />
						<S.Mask style={maskStyles.bottom} />
						<S.Mask style={maskStyles.left} />
						<S.Mask style={maskStyles.right} />
					</>
				);
			})()}
			{(() => {
				const frameStyles = getFrameStyles(bounds);
				return (
					<>
						<S.FrameTop style={frameStyles.top} highlighted={prototype} />
						<S.FrameBottom style={frameStyles.bottom} highlighted={prototype} />
						<S.FrameLeft style={frameStyles.left} highlighted={prototype} />
						<S.FrameRight style={frameStyles.right} highlighted={prototype} />
					</>
				);
			})()}
			<S.ControlBox
				style={{
					left: bounds.x,
					top: bounds.y - 26,
					width: bounds.width,
				}}
			>
				<S.Icon>
					<Icon type={getFrameIconType(frame)} width={12} height={12} color={unthemed.white} />
				</S.Icon>
				<S.FrameTitle style={{ fontSize: '12px' }}>{frame.title}</S.FrameTitle>
				{controlType === ControlType.FRAME ? renderFrameActions() : null}
			</S.ControlBox>
			<S.ControlBox
				style={{
					left: bounds.x,
					top: bounds.y + bounds.height + 7,
					width: bounds.width,
				}}
			>
				{renderArrowActions()}
			</S.ControlBox>
		</React.Fragment>
	);
};

export default FrameControls;
